import { BrandDemo } from '@/components/BrandDemo'
import { OKLCHDemo } from '@/components/OKLCHDemo'
import { ColorCustomizer } from '@/components/ColorCustomizer'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { brandColors, gradientClasses } from '@/theme/brandColors'
import { useAppSelector } from '@redux/hooks/useAppState'
import { Theme } from '@/theme'
import { verifyBrandColors } from '@/utils/verifyColors'
import { testBrandColorConversions } from '@/utils/oklchConverter'
import { debugCSSVariables } from '@/utils/debugColors'
import { useEffect } from 'react'

export const ThemeDemo = () => {
	const theme = useAppSelector((state) => state.theme.theme)
	const isDarkMode = theme === Theme.Dark

	// Verify colors in console
	useEffect(() => {
		verifyBrandColors()
		testBrandColorConversions()
		debugCSSVariables()
	}, [theme])

	return (
		<div className="p-8 space-y-8">
			<div className="max-w-6xl mx-auto">
				<h1 className="text-4xl font-bold mb-2">HoneyGrid Theme Demo</h1>
				<p className="text-lg text-muted-foreground mb-8">
					Current theme: <strong>{isDarkMode ? 'Dark' : 'Light'}</strong>
				</p>

				{/* Theme Toggle Instructions */}
				<Card className="p-6 mb-8">
					<h2 className="text-2xl font-semibold mb-4">Theme Toggle</h2>
					<p className="text-muted-foreground mb-4">
						Use the theme toggle in the sidebar to switch between light and dark modes. 
						The toggle is located below the "Collapse sidebar" button.
					</p>
					<div className="space-y-4">
						<div className="flex gap-4 flex-wrap">
							<Button variant="default">Primary Button</Button>
							<Button variant="secondary">Secondary Button</Button>
							<Button variant="outline">Outline Button</Button>
							<Button variant="ghost">Ghost Button</Button>
							<Button variant="destructive">Destructive</Button>
							<Button variant="link">Link Button</Button>
						</div>
						<div className="flex gap-4 flex-wrap">
							<Button size="sm">Small</Button>
							<Button size="default">Default</Button>
							<Button size="lg">Large</Button>
							<Button size="icon">🎨</Button>
						</div>
						<p className="text-sm text-muted-foreground mt-2">
							Primary buttons use {brandColors.primaryAction} (#621984)<br/>
							Secondary buttons use {brandColors.primaryLight} (#81479C)
						</p>
					</div>
				</Card>

				{/* Color Showcase */}
				<Card className="p-6 mb-8">
					<h2 className="text-2xl font-semibold mb-4">Theme Colors</h2>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div className="space-y-2">
							<div className="h-24 rounded-lg bg-primary" />
							<p className="text-sm font-medium">Primary</p>
							<p className="text-xs text-muted-foreground">
								{isDarkMode ? brandColors.primaryLight : brandColors.primaryAction}
							</p>
						</div>
						<div className="space-y-2">
							<div className="h-24 rounded-lg bg-secondary" />
							<p className="text-sm font-medium">Secondary</p>
							<p className="text-xs text-muted-foreground">{brandColors.hexGrid}</p>
						</div>
						<div className="space-y-2">
							<div className="h-24 rounded-lg bg-background border" />
							<p className="text-sm font-medium">Background</p>
							<p className="text-xs text-muted-foreground">
								{isDarkMode ? brandColors.primary : brandColors.primaryContrast}
							</p>
						</div>
						<div className="space-y-2">
							<div className="h-24 rounded-lg bg-muted" />
							<p className="text-sm font-medium">Muted</p>
						</div>
					</div>
				</Card>

				{/* Gradient Showcase */}
				<Card className="p-6 mb-8">
					<h2 className="text-2xl font-semibold mb-4">Brand Gradient</h2>
					<div className={`h-32 rounded-lg ${gradientClasses.gradient}`} />
					<p className="text-sm text-muted-foreground mt-2">
						The brand gradient remains vibrant in both light and dark modes
					</p>
				</Card>

				{/* Typography */}
				<Card className="p-6 mb-8">
					<h2 className="text-2xl font-semibold mb-4">Typography</h2>
					<div className="space-y-4">
						<h1 className="text-4xl font-bold">Heading 1</h1>
						<h2 className="text-3xl font-semibold">Heading 2</h2>
						<h3 className="text-2xl font-semibold">Heading 3</h3>
						<p className="text-base">Regular paragraph text</p>
						<p className="text-sm text-muted-foreground">Muted text</p>
					</div>
				</Card>

				{/* Color Customizer */}
				<ColorCustomizer />

				{/* OKLCH Demo */}
				<OKLCHDemo />

				{/* Full Brand Demo */}
				<Card className="p-6">
					<BrandDemo />
				</Card>
			</div>
		</div>
	)
}